/**
 * ContractActionDialogBase
 * Shared template for all contract action dialogs (Extend, Rollover, Capital Redemption, Terminate).
 * Provides consistent layout: contract info header, action fields (children), reason, proof upload, action buttons.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { apiUpload } from '../../utils/apiClient';
import logger from '../../utils/logger';
import { formatNumber } from '../../utils/formatters';
import { useTenant } from '../../contexts/TenantContext';
import FileDropZone from '../common/FileDropZone';

const ContractActionDialogBase = ({
  open,
  onClose,
  contract,
  title,
  description,
  submitLabel = 'Submit for Approval',
  submitColor = 'primary',
  cancelLabel = 'Cancel',
  previewLabel = 'Preview Impact',
  isValid = false,
  submitting = false,
  error = null,
  onClearError,
  onSubmit,
  onPreview,
  previewLoading = false,
  previewContent,
  showProofUpload = true,
  showPreview = false,
  workflowCode,
  reason,
  onReasonChange,
  reasonLabel = 'Reason (Required)',
  reasonPlaceholder = 'Provide a detailed reason for this action. This will be reviewed by an approver.',
  reasonHelperText = 'A reason is mandatory and will be reviewed by an approver.',
  children,
  maxWidth = 'lg',
}) => {
  const { tenant, isWorkflowDocumentRequired } = useTenant();
  const dp = tenant?.roundingPrecision ?? 3;
  const [proofFile, setProofFile] = useState(null);
  const proofRequired = workflowCode ? isWorkflowDocumentRequired(workflowCode) : false;

  // Upload proof document and return IDs
  const uploadProofDocument = async () => {
    if (!proofFile) return [];
    try {
      const formData = new FormData();
      formData.append('file', proofFile);
      formData.append('document_type', 'proof_of_amendment');
      formData.append('contract_id', contract.id);
      formData.append('notes', `${title}: ${reason}`);
      const result = await apiUpload('/admin/documents/upload', formData);
      return result.data?.id ? [result.data.id] : [];
    } catch (err) {
      logger.error('Proof upload error', { message: err.message });
      return [];
    }
  };

  const handleSubmit = async () => {
    // Defense-in-depth: the submit button is already disabled in this state,
    // but a stale render or future refactor could let the click through.
    if (proofRequired && !proofFile) {
      return;
    }
    const proofDocumentIds = await uploadProofDocument();
    onSubmit({ proof_document_ids: proofDocumentIds });
  };

  const handleClose = () => {
    setProofFile(null);
    onClose();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'dd MMM yyyy');
    } catch { return dateStr; }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth={maxWidth} fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight="bold">{title}</Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {/* Description */}
        {description && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {description}
          </Alert>
        )}

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={onClearError}>
            {error}
          </Alert>
        )}

        {/* Contract Info Header */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: '#f5f5f5' }}>
          <Grid container spacing={1}>
            <Grid
              size={{
                xs: 6,
                sm: 3
              }}>
              <Typography variant="caption" color="textSecondary">Contract</Typography>
              <Typography variant="body2" fontWeight="medium">
                {contract?.reference || contract?.contract_number || '-'}
              </Typography>
            </Grid>
            <Grid
              size={{
                xs: 6,
                sm: 2
              }}>
              <Typography variant="caption" color="textSecondary">Status</Typography>
              <Typography variant="body2" fontWeight="medium">{contract?.status || '-'}</Typography>
            </Grid>
            <Grid
              size={{
                xs: 6,
                sm: 2
              }}>
              <Typography variant="caption" color="textSecondary">Rate</Typography>
              <Typography variant="body2" fontWeight="medium">{contract?.interest_rate}%</Typography>
            </Grid>
            <Grid
              size={{
                xs: 6,
                sm: 2
              }}>
              <Typography variant="caption" color="textSecondary">Maturity</Typography>
              <Typography variant="body2" fontWeight="medium">{formatDate(contract?.maturity_date)}</Typography>
            </Grid>
            <Grid
              size={{
                xs: 6,
                sm: 3
              }}>
              <Typography variant="caption" color="textSecondary">Balance</Typography>
              <Typography variant="body2" fontWeight="medium">
                {contract?.currency_code || ''} {formatNumber(contract?.capital_amount, dp)}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Action-Specific Fields (passed as children) */}
        {children}

        {/* Impact Preview */}
        {previewContent && (
          <>
            <Divider sx={{ my: 2 }} />
            {previewContent}
          </>
        )}

        {/* Reason / Motivation */}
        <Box sx={{ mt: 2 }}>
          <TextField
            label={reasonLabel}
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
            fullWidth
            required
            multiline
            rows={3}
            placeholder={reasonPlaceholder}
            error={!reason && submitting}
            helperText={reasonHelperText}
          />
        </Box>

        {/* Proof Document Upload */}
        {showProofUpload && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Supporting Documentation{proofRequired ? ' (Required)' : ' (Optional)'}
            </Typography>
            <FileDropZone
              file={proofFile}
              onFile={(f) => setProofFile(f)}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
              helperText="PDF, JPG, PNG, DOC, DOCX, XLS, XLSX"
              promptText="Click to upload proof document, or drag and drop"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={submitting}>
          {cancelLabel}
        </Button>
        {showPreview && onPreview && (
          <Button
            variant="outlined"
            onClick={onPreview}
            disabled={previewLoading || !isValid}
            startIcon={previewLoading ? <CircularProgress size={16} /> : null}
          >
            {previewLoading ? 'Calculating...' : previewLabel}
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isValid || !reason || submitting || (proofRequired && !proofFile)}
          startIcon={submitting ? <CircularProgress size={16} /> : null}
          color={submitColor}
        >
          {submitting ? 'Submitting...' : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContractActionDialogBase;
